import React, { ReactElement, useState } from 'react'
import { Box, Button, Chip, TextField, Typography, Alert } from '@mui/material'
import { useForm } from '@tanstack/react-form'

import { useAppDispatch } from 'src/store/hooks'
import {
  updateSyllabus,
  fetchEventCompliances,
  fetchSyllabus,
} from 'src/v3/content-pipeline/event-pipeline.slice'
import { ContentComplianceStatus, Syllabus } from 'src/v3/core/types'

type SyllabusFieldEditableProps = {
  eventID: string
  syllabus: Syllabus
  fieldName: keyof Pick<
    Syllabus,
    | 'text'
    | 'practiceGap'
    | 'practiceGapEvidence'
    | 'learnerEngagement'
    | 'learningOutcomes'
  >
  fieldLabel: string
  fieldType: 'text' | 'textarea'
  complianceStatus?: ContentComplianceStatus
  complianceFeedback?: string
  required?: boolean
  viewMode?: 'inline' | 'modal' // inline for planning, modal for compliance
  onClose?: () => void // Called when modal is closed (modal mode only)
}

let statusColors: Record<ContentComplianceStatus, any> = {
  new: 'primary',
  approved: 'success',
  rejected: 'error',
  updated: 'primary',
  unapproved: 'error',
}

let statusLabels: Record<ContentComplianceStatus, string> = {
  new: 'Pending Review',
  approved: 'Approved',
  rejected: 'Rejected',
  updated: 'Updated - Pending Review',
  unapproved: 'Unapproved',
}

export default ({
  eventID,
  syllabus,
  fieldName,
  fieldLabel,
  fieldType,
  complianceStatus,
  complianceFeedback,
  required = false,
  viewMode = 'inline',
  onClose,
}: SyllabusFieldEditableProps): ReactElement => {
  let dispatch = useAppDispatch()
  let [isEditing, setIsEditing] = useState(false)
  let [loading, setLoading] = useState(false)

  let currentValue = syllabus?.[fieldName] || ''

  let form = useForm({
    defaultValues: {
      [fieldName]: currentValue,
    },
    onSubmit: async ({ value }) => {
      setLoading(true)
      try {
        let updateValue = value[fieldName]
        let fieldUpdates: any = {
          id: syllabus.id,
          [fieldName]: updateValue,
        }

        // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
        await dispatch(updateSyllabus(fieldUpdates))
        // Refetch both syllabus and compliance statuses
        // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
        await dispatch(fetchSyllabus(eventID))
        // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
        await dispatch(fetchEventCompliances())
        setIsEditing(false)

        // Close modal if in modal mode
        if (viewMode === 'modal' && onClose) {
          onClose()
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to update syllabus field:', error)
      } finally {
        setLoading(false)
      }
    },
  })

  let renderFieldDisabled = () => {
    if (fieldType === 'textarea') {
      return (
        <TextField
          sx={{ bgcolor: 'background.paperDark' }}
          fullWidth
          multiline
          rows={4}
          value={currentValue || ''}
          placeholder="Not set"
          disabled
        />
      )
    }

    // Default text field
    return (
      <TextField
        sx={{ bgcolor: 'background.paperDark' }}
        fullWidth
        value={currentValue || ''}
        placeholder="Not set"
        disabled
        aria-label={fieldLabel}
        aria-describedby={
          complianceStatus ? `${fieldName}-compliance-status` : undefined
        }
      />
    )
  }

  let renderField = () => {
    if (fieldType === 'textarea') {
      return (
        <form.Field
          name={fieldName}
          children={(field) => (
            <TextField
              sx={{ bgcolor: 'background.paperDark' }}
              fullWidth
              multiline
              rows={4}
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
              disabled={loading}
            />
          )}
        />
      )
    }

    // Default text field
    return (
      <form.Field
        name={fieldName}
        children={(field) => (
          <TextField
            sx={{ bgcolor: 'background.paperDark' }}
            fullWidth
            value={field.state.value}
            onChange={(e) => field.handleChange(e.target.value)}
            onBlur={field.handleBlur}
            disabled={loading}
            aria-label={fieldLabel}
            aria-describedby={
              complianceStatus ? `${fieldName}-compliance-status` : undefined
            }
          />
        )}
      />
    )
  }

  let renderContent = () => (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: 'medium',
          }}
        >
          {fieldLabel}
          {required && (
            <Typography color="error" component="span" aria-label="required">
              {' '}
              *
            </Typography>
          )}
        </Typography>
        {complianceStatus && (
          <Chip
            size="small"
            label={statusLabels[complianceStatus]}
            variant="outlined"
            id={`${fieldName}-compliance-status`}
            aria-label={`Compliance status: ${statusLabels[complianceStatus]}`}
            color={statusColors[complianceStatus]}
          />
        )}
      </Box>

      {!isEditing ? (
        <Box sx={{ position: 'relative' }}>
          {renderFieldDisabled()}
          <Button
            variant="text"
            size="small"
            onClick={() => {
              form.setFieldValue(fieldName, currentValue)
              setIsEditing(true)
            }}
            sx={{
              position: 'absolute',
              right: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              minWidth: 'auto',
              px: 1,
            }}
            aria-label={`Edit ${fieldLabel}`}
          >
            Edit
          </Button>
        </Box>
      ) : (
        <Box>
          <Box sx={{ mb: 2 }}>{renderField()}</Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              onClick={() => form.handleSubmit()}
              disabled={loading}
              aria-label={`Save changes to ${fieldLabel}`}
            >
              {loading ? 'Saving...' : 'Save'}
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setIsEditing(false)
                form.reset()
                // Close modal if in modal mode
                if (viewMode === 'modal' && onClose) {
                  onClose()
                }
              }}
              disabled={loading}
              aria-label={`Cancel editing ${fieldLabel}`}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      )}

      {complianceFeedback &&
        (complianceStatus === 'rejected' ||
          complianceStatus === 'unapproved') && (
          <Alert
            severity="warning"
            sx={{ mt: 2 }}
            role="alert"
            aria-live="polite"
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 'medium',
              }}
            >
              Compliance Feedback:
            </Typography>
            <Typography variant="body2">{complianceFeedback}</Typography>
          </Alert>
        )}
    </Box>
  )

  // If modal mode, render the content directly (parent will wrap in modal)
  if (viewMode === 'modal') {
    return renderContent()
  }

  // Inline mode - render directly
  return renderContent()
}
